const ACTIVITY_TYPES = Object.freeze(["note", "todo", "call", "email", "meeting"]);
const ENTITY_TYPES = Object.freeze(["company", "person", "deal", "contract", "user"]);
const ORIGIN_ENTITY_TYPES = Object.freeze(["lead_entry"]);
const ASSIGNMENT_SOURCES = Object.freeze(["user", "system", "import"]);
const DEFAULT_ASSIGNMENT_SOURCE = "user";
const ACTIVITY_PRIORITIES = Object.freeze(["low", "normal", "high"]);
const PROGRAMMABLE_ACTIVITY_TYPES = Object.freeze(["todo", "call", "email", "meeting"]);
const DEFAULT_ACTIVITY_TYPE = "note";
const DEFAULT_ACTIVITY_PRIORITY = "normal";

const STATUS_BY_TYPE = Object.freeze({
  note: Object.freeze(["logged"]),
  todo: Object.freeze(["open", "in_progress", "done", "canceled"]),
  call: Object.freeze(["planned", "done", "no_answer", "canceled"]),
  email: Object.freeze(["planned", "sent", "replied", "failed", "canceled"]),
  meeting: Object.freeze(["planned", "held", "no_show", "canceled"]),
});
const ALL_ACTIVITY_STATUSES = Object.freeze(
  Object.values(STATUS_BY_TYPE)
    .flat()
    .reduce((acc, status) => (acc.includes(status) ? acc : [...acc, status]), [])
);

const OPEN_STATUSES_BY_TYPE = Object.freeze({
  note: Object.freeze([]),
  todo: Object.freeze(["open", "in_progress"]),
  call: Object.freeze(["planned"]),
  email: Object.freeze(["planned"]),
  meeting: Object.freeze(["planned"]),
});

const DEFAULT_CLOSED_STATUS_BY_TYPE = Object.freeze({
  note: "logged",
  todo: "done",
  call: "done",
  email: "sent",
  meeting: "held",
});

const toText = (value) => String(value ?? "").trim();

export const isSupportedNcrmActivityType = (value) => {
  return ACTIVITY_TYPES.includes(toText(value).toLowerCase());
};

export const normalizeNcrmActivityType = (value, fallback = DEFAULT_ACTIVITY_TYPE) => {
  const normalized = toText(value).toLowerCase();
  if (!normalized) {
    if (fallback === null || fallback === false) return null;
    return isSupportedNcrmActivityType(fallback) ? fallback : DEFAULT_ACTIVITY_TYPE;
  }
  return isSupportedNcrmActivityType(normalized) ? normalized : null;
};

export const isNcrmActivityProgrammableType = (activityType) => {
  const normalizedType = normalizeNcrmActivityType(activityType);
  return Boolean(normalizedType && PROGRAMMABLE_ACTIVITY_TYPES.includes(normalizedType));
};

export const resolveNcrmActivityDefaultStatus = (activityType) => {
  const normalizedType = normalizeNcrmActivityType(activityType);
  if (normalizedType === "note") return "logged";
  if (normalizedType === "todo") return "open";
  if (!normalizedType) return "logged";
  return "planned";
};

export const resolveNcrmActivityDefaultClosedStatus = (activityType) => {
  const normalizedType = normalizeNcrmActivityType(activityType);
  if (!normalizedType) return "done";
  return DEFAULT_CLOSED_STATUS_BY_TYPE[normalizedType] || "done";
};

export const getNcrmActivityStatusOptions = (activityType) => {
  const normalizedType = normalizeNcrmActivityType(activityType);
  if (!normalizedType) return [];
  return (STATUS_BY_TYPE[normalizedType] || []).slice();
};

export const normalizeNcrmActivityStatus = (value, activityType, fallback = true) => {
  const normalizedType = normalizeNcrmActivityType(activityType, null);
  const normalized = toText(value).toLowerCase();
  const allowed = normalizedType ? STATUS_BY_TYPE[normalizedType] || [] : [];

  if (!normalized) {
    return fallback && normalizedType ? resolveNcrmActivityDefaultStatus(normalizedType) : null;
  }
  if (!normalizedType) return ALL_ACTIVITY_STATUSES.includes(normalized) ? normalized : null;
  return allowed.includes(normalized) ? normalized : null;
};

export const isNcrmActivityOpenStatus = (activityType, status) => {
  const normalizedType = normalizeNcrmActivityType(activityType);
  const normalizedStatus = normalizeNcrmActivityStatus(status, normalizedType, false);
  if (!normalizedType || !normalizedStatus) return false;
  const openStatuses = OPEN_STATUSES_BY_TYPE[normalizedType] || [];
  return openStatuses.includes(normalizedStatus);
};

export const getNcrmActivityPriorityOptions = () => {
  return ACTIVITY_PRIORITIES.slice();
};

export const normalizeNcrmActivityPriority = (value, fallback = DEFAULT_ACTIVITY_PRIORITY) => {
  const normalized = toText(value).toLowerCase();
  if (!normalized) {
    return ACTIVITY_PRIORITIES.includes(fallback) ? fallback : DEFAULT_ACTIVITY_PRIORITY;
  }
  return ACTIVITY_PRIORITIES.includes(normalized) ? normalized : null;
};

export const normalizeNcrmActivityEntityType = (value) => {
  const normalized = toText(value).toLowerCase();
  return ENTITY_TYPES.includes(normalized) ? normalized : null;
};

export const normalizeNcrmActivityOriginEntityType = (value) => {
  const normalized = toText(value).toLowerCase();
  return ORIGIN_ENTITY_TYPES.includes(normalized) ? normalized : null;
};

export const normalizeNcrmAssignmentSource = (value, fallback = DEFAULT_ASSIGNMENT_SOURCE) => {
  const normalized = toText(value).toLowerCase();
  if (!normalized) {
    return ASSIGNMENT_SOURCES.includes(fallback) ? fallback : DEFAULT_ASSIGNMENT_SOURCE;
  }
  return ASSIGNMENT_SOURCES.includes(normalized) ? normalized : DEFAULT_ASSIGNMENT_SOURCE;
};

export const parseNcrmActivityEntityId = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : null;
};

export const normalizeNcrmActivityDateTimeInput = (value) => {
  const normalized = toText(value);
  if (!normalized) return "";
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? "" : normalized;
};

export const buildNcrmActivityCreatePayload = (payload = {}) => {
  const entityType = normalizeNcrmActivityEntityType(payload.entityType ?? payload.entity_type);
  const entityId = parseNcrmActivityEntityId(payload.entityId ?? payload.entity_id);
  const ownerUserId = parseNcrmActivityEntityId(payload.ownerUserId ?? payload.owner_user_id);
  const activityType = normalizeNcrmActivityType(payload.activityType ?? payload.activity_type);
  const resolvedType = activityType || DEFAULT_ACTIVITY_TYPE;
  const status = normalizeNcrmActivityStatus(payload.status, resolvedType, true);
  const priority = normalizeNcrmActivityPriority(payload.priority, DEFAULT_ACTIVITY_PRIORITY);
  const title = toText(payload.title);
  const content = toText(payload.content ?? payload.body ?? payload.note_text);
  const happenedAt = normalizeNcrmActivityDateTimeInput(payload.happenedAt ?? payload.happened_at);
  const dueAt = normalizeNcrmActivityDateTimeInput(payload.dueAt ?? payload.due_at);
  const originEntityType = normalizeNcrmActivityOriginEntityType(
    payload.originEntityType ?? payload.origin_entity_type
  );
  const originEntityId = parseNcrmActivityEntityId(payload.originEntityId ?? payload.origin_entity_id);

  const result = {
    entity_type: entityType,
    entity_id: entityId,
    activity_type: resolvedType,
    title: title || null,
    content: content || "",
  };

  if (ownerUserId) result.owner_user_id = ownerUserId;

  if (resolvedType !== "note") {
    result.status = status || resolveNcrmActivityDefaultStatus(resolvedType);
    result.priority = priority || DEFAULT_ACTIVITY_PRIORITY;
  }

  if (originEntityType && originEntityId) {
    result.origin_entity_type = originEntityType;
    result.origin_entity_id = originEntityId;
  }

  if (happenedAt) result.happened_at = happenedAt;
  if (dueAt) result.due_at = dueAt;

  return result;
};

export const resolveNcrmActivityDisplayTitle = (item = {}, { maxLength = 80, fallback = "" } = {}) => {
  const title = toText(item.title);
  if (title) return title;
  const content = toText(item.content ?? item.body ?? item.note_text).replace(/\s+/g, " ");
  if (!content) return fallback;
  if (!Number.isFinite(maxLength) || maxLength <= 0 || content.length <= maxLength) return content;
  return `${content.slice(0, maxLength).trimEnd()}…`;
};

const parseNcrmActivityTypePayload = (value) => {
  if (!value) return {};
  if (typeof value === "object") return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

export const normalizeNcrmActivityItem = (item = {}) => {
  const id = parseNcrmActivityEntityId(item.id) || null;
  const activityType = normalizeNcrmActivityType(item.activity_type ?? item.activityType);
  const normalizedType = activityType || DEFAULT_ACTIVITY_TYPE;
  const status = normalizeNcrmActivityStatus(item.status, normalizedType, true);
  const priority = normalizeNcrmActivityPriority(item.priority, DEFAULT_ACTIVITY_PRIORITY);
  return {
    id,
    entity_type: normalizeNcrmActivityEntityType(item.entity_type ?? item.entityType),
    entity_id: parseNcrmActivityEntityId(item.entity_id ?? item.entityId),
    onboarding_id: parseNcrmActivityEntityId(item.onboarding_id ?? item.onboardingId),
    origin_entity_type: normalizeNcrmActivityOriginEntityType(item.origin_entity_type ?? item.originEntityType),
    origin_entity_id: parseNcrmActivityEntityId(item.origin_entity_id ?? item.originEntityId),
    origin_board_id: parseNcrmActivityEntityId(item.origin_board_id ?? item.originBoardId),
    origin_board_name: toText(item.origin_board_name ?? item.originBoardName),
    origin_stage_id: parseNcrmActivityEntityId(item.origin_stage_id ?? item.originStageId),
    origin_stage_name: toText(item.origin_stage_name ?? item.originStageName),
    origin_lead_status: toText(item.origin_lead_status ?? item.originLeadStatus).toLowerCase(),
    activity_type: normalizedType,
    status: status || resolveNcrmActivityDefaultStatus(normalizedType),
    priority: priority || DEFAULT_ACTIVITY_PRIORITY,
    owner_user_id: parseNcrmActivityEntityId(item.owner_user_id ?? item.ownerUserId),
    owner_name: toText(item.owner_name ?? item.ownerName),
    assigned_by_user_id: parseNcrmActivityEntityId(item.assigned_by_user_id ?? item.assignedByUserId),
    assigned_by_name: toText(item.assigned_by_name ?? item.assignedByName),
    assignment_source: normalizeNcrmAssignmentSource(item.assignment_source ?? item.assignmentSource),
    assigned_at: toText(item.assigned_at ?? item.assignedAt),
    entity_name: toText(item.entity_name ?? item.entityName),
    title: toText(item.title),
    content: toText(item.content ?? item.body ?? item.note_text),
    happened_at: toText(item.happened_at ?? item.happenedAt),
    due_at: toText(item.due_at ?? item.dueAt),
    closed_at: toText(item.closed_at ?? item.closedAt),
    closed_by_user_id: parseNcrmActivityEntityId(item.closed_by_user_id ?? item.closedByUserId),
    created_at: toText(item.created_at ?? item.createdAt),
    updated_at: toText(item.updated_at ?? item.updatedAt),
    created_by_user_id: parseNcrmActivityEntityId(item.created_by_user_id ?? item.createdByUserId),
    updated_by_user_id: parseNcrmActivityEntityId(item.updated_by_user_id ?? item.updatedByUserId),
    type_payload: parseNcrmActivityTypePayload(item.type_payload ?? item.typePayload),
  };
};

export const normalizeNcrmActivityList = (value) => {
  const source = Array.isArray(value) ? value : Array.isArray(value?.items) ? value.items : [];
  return source.map((item) => normalizeNcrmActivityItem(item));
};

export const getNcrmActivityTypeOptions = () => {
  return ACTIVITY_TYPES.slice();
};
